<template>
  <div class="bill-config-content">
    <el-alert
      title="自定义账单配置"
      type="info"
      description="在此配置各Link的返点比例、Support费用等信息"
      :closable="false"
      style="margin-bottom: 20px"
    />

    <!-- 筛选条件 -->
    <el-form :inline="true" :model="filters" size="small">
      <el-form-item label="月份">
        <el-date-picker
          v-model="filters.month"
          type="month"
          placeholder="选择月份"
          format="yyyy-MM"
          value-format="yyyyMM"
        />
      </el-form-item>

      <el-form-item label="Link ID">
        <el-input v-model="filters.link_id" placeholder="输入Link ID" clearable />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="queryConfig">查询</el-button>
        <el-button icon="el-icon-plus" @click="showAddDialog">新增配置</el-button>
      </el-form-item>
    </el-form>

    <!-- 配置列表 -->
    <el-table v-loading="loading" :data="configList" stripe style="width: 100%">
      <el-table-column prop="linkId" label="Link ID" width="150" />
      <el-table-column prop="month" label="月份" width="100" />
      <el-table-column prop="discount2" label="返点比例" width="120">
        <template slot-scope="scope">
          {{ (scope.row.discount2 * 100).toFixed(2) }}%
        </template>
      </el-table-column>
      <el-table-column prop="sb_discount2" label="Support返点" width="120">
        <template slot-scope="scope">
          {{ (scope.row.sb_discount2 * 100).toFixed(2) }}%
        </template>
      </el-table-column>
      <el-table-column prop="creditDeductionMethod" label="抵扣方式" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.creditDeductionMethod === 'before' ? 'success' : 'info'" size="small">
            {{ scope.row.creditDeductionMethod === 'before' ? '先抵扣' : '后抵扣' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="160" />
      <el-table-column label="操作" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="editConfig(scope.row)">编辑</el-button>
          <el-button type="text" size="small" style="color: red" @click="deleteConfig(scope.row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="total > 0"
      :current-page="page"
      :page-size="pageSize"
      :total="total"
      layout="total, prev, pager, next"
      style="margin-top: 20px; text-align: right"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="120px">
        <el-form-item label="Link ID" prop="linkId">
          <el-input v-model="form.linkId" :disabled="isEdit" placeholder="请输入Link ID" />
        </el-form-item>

        <el-form-item label="月份" prop="month">
          <el-date-picker
            v-model="form.month"
            type="month"
            placeholder="选择月份"
            format="yyyy-MM"
            value-format="yyyyMM"
            :disabled="isEdit"
          />
        </el-form-item>

        <el-form-item label="返点比例" prop="discount2">
          <el-input-number
            v-model="form.discount2"
            :min="0"
            :max="1"
            :step="0.01"
            :precision="4"
            placeholder="0.22"
          />
          <span style="margin-left: 10px; color: #909399">
            {{ (form.discount2 * 100).toFixed(2) }}%
          </span>
        </el-form-item>

        <el-form-item label="Support返点" prop="sb_discount2">
          <el-input-number
            v-model="form.sb_discount2"
            :min="0"
            :max="1"
            :step="0.01"
            :precision="4"
            placeholder="0.10"
          />
          <span style="margin-left: 10px; color: #909399">
            {{ (form.sb_discount2 * 100).toFixed(2) }}%
          </span>
        </el-form-item>

        <el-form-item label="抵扣方式" prop="creditDeductionMethod">
          <el-radio-group v-model="form.creditDeductionMethod">
            <el-radio label="before">先抵扣</el-radio>
            <el-radio label="after">后抵扣</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'BillConfigContent',
  data() {
    return {
      filters: {
        month: '',
        link_id: ''
      },
      configList: [],
      loading: false,
      page: 1,
      pageSize: 20,
      total: 0,
      dialogVisible: false,
      dialogTitle: '新增配置',
      isEdit: false,
      form: {
        linkId: '',
        month: '',
        discount2: 0,
        sb_discount2: 0,
        creditDeductionMethod: 'after'
      },
      rules: {
        linkId: [{ required: true, message: '请输入Link ID', trigger: 'blur' }],
        month: [{ required: true, message: '请选择月份', trigger: 'change' }],
        discount2: [{ required: true, message: '请输入返点比例', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.queryConfig()
  },
  methods: {
    async queryConfig() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize,
          month: this.filters.month,
          link_id: this.filters.link_id
        }

        const response = await this.$http.get('/bill/bill-config/', { params })

        if (response.data.status === 'success') {
          this.configList = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        this.$message.error('查询失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    handlePageChange(page) {
      this.page = page
      this.queryConfig()
    },

    showAddDialog() {
      this.dialogTitle = '新增配置'
      this.isEdit = false
      this.form = {
        linkId: '',
        month: '',
        discount2: 0,
        sb_discount2: 0,
        creditDeductionMethod: 'after'
      }
      this.dialogVisible = true
    },

    editConfig(row) {
      this.dialogTitle = '编辑配置'
      this.isEdit = true
      this.form = { ...row }
      this.dialogVisible = true
    },

    async deleteConfig(row) {
      try {
        await this.$confirm('确定要删除此配置吗？', '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await this.$http.delete(`/bill/bill-config/${row.id}/`)

        if (response.data.status === 'success') {
          this.$message.success('删除成功')
          this.queryConfig()
        } else {
          this.$message.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }
    },

    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        try {
          const url = this.isEdit
            ? `/bill/bill-config/${this.form.id}/`
            : '/bill/bill-config/'

          const method = this.isEdit ? 'put' : 'post'

          const response = await this.$http[method](url, this.form)

          if (response.data.status === 'success') {
            this.$message.success(this.isEdit ? '更新成功' : '创建成功')
            this.dialogVisible = false
            this.queryConfig()
          } else {
            this.$message.error(response.data.message || '操作失败')
          }
        } catch (error) {
          this.$message.error('操作失败: ' + (error.response?.data?.message || error.message))
        }
      })
    }
  }
}
</script>

<style scoped>
.bill-config-content {
  padding: 10px;
}
</style>
